"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/trpc/client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { FileText, Frown, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notification } from "@/components/institution/Notification";
import { useLocale } from "use-intl";
import { translations } from "@/messages";
interface PublicFormsListProps {
  institutionSlug: string;
  baseUrl: string;
  className?: string;
}
export const PublicFormsList: React.FC<PublicFormsListProps> = ({
  institutionSlug,
  baseUrl,
  className
}) => {
  const locale = useLocale() as keyof typeof translations;
  const [selectedCategoriesIds, setSelectedCategoriesIds] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const formsPerCategory = api.formManagementRouter.getInstitutionFormsWithCategories.useQuery({
    institutionSlug
  }, {
    staleTime: Infinity,
    enabled: !!institutionSlug?.length
  });
  useEffect(() => {
    if (formsPerCategory.data) {
      const firstCategory = formsPerCategory.data.sort((a, b) => a.displayName.localeCompare(b.displayName))[0]?.categoryId;
      if (firstCategory) {
        setSelectedCategoriesIds([firstCategory]);
      }
    }
  }, [formsPerCategory.data]);
  if (!formsPerCategory.data) {
    return null;
  }
  const shouldExpandAccordion = searchValue.trim().length > 0;
  const filteredFormsData = formsPerCategory.data.map(category => ({
    ...category,
    forms: category.forms.filter(form => form.displayName.toLowerCase().includes(searchValue.toLowerCase()))
  }));
  const sortedCategories = filteredFormsData.sort((a, b) => a.displayName.localeCompare(b.displayName)).filter(category => category.forms.length > 0);
  const allCategoriesIds = sortedCategories.map(category => category.categoryId);
  const noForms = sortedCategories.length === 0;
  return <Card className={`h-auto tablet:overflow-hidden tablet:overflow-y-auto ${className}`} data-sentry-element="Card" data-sentry-component="PublicFormsList" data-sentry-source-file="PublicFormsList.tsx">
      <CardHeader className="flex flex-col items-start justify-between gap-2 tablet:flex-row tablet:items-center" data-sentry-element="CardHeader" data-sentry-source-file="PublicFormsList.tsx">
        <div>
          <CardTitle className="text-glow" data-sentry-element="CardTitle" data-sentry-source-file="PublicFormsList.tsx">
            {translations[locale]["institution"]["submitDocument"]}
          </CardTitle>
          <CardDescription className="mt-1" data-sentry-element="CardDescription" data-sentry-source-file="PublicFormsList.tsx">
            {translations[locale]["institution"]["chooseFormType"]}
          </CardDescription>
        </div>

        <div className="relative w-full tablet:w-auto">
          <input type="text" placeholder={translations[locale]["institution"]["searchForm"]} value={searchValue} onChange={e => setSearchValue(e.target.value)} className="w-full rounded-3xl border bg-secondary/40 p-2 pl-10 tablet:w-auto" />
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" data-sentry-element="Search" data-sentry-source-file="PublicFormsList.tsx" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2" data-sentry-element="CardContent" data-sentry-source-file="PublicFormsList.tsx">
        {sortedCategories.map(category => category.displayName !== "ReportMapProblem" && <div className="h-full" key={category.categoryId}>
                <Accordion type="multiple" className="h-full w-full" value={shouldExpandAccordion ? allCategoriesIds : selectedCategoriesIds} onValueChange={setSelectedCategoriesIds}>
                  <AccordionItem value={category.categoryId}>
                    <AccordionTrigger chevronPosition="start">
                      <h2 className="text-base font-bold text-primary/80">
                        {category.displayName}
                      </h2>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="flex flex-col gap-1">
                        {category.forms.map(form => <div key={form.id} className="grid gap-3">
                            <Link href={`${baseUrl}/${form.slug}`} passHref>
                              <button className="inline-flex h-auto min-h-[2.5rem] w-full animate-none items-center justify-between gap-3 rounded-3xl bg-transparent px-3 py-6 text-sm font-medium text-primary ring-offset-background transition-colors duration-200 ease-linear hover:bg-glow/20 hover:text-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 mobile:py-3">
                                <div className="flex items-center gap-2 text-left">
                                  <FileText width={18} height={18} className="min-w-[20px]" />
                                  {form.displayName}
                                </div>
                              </button>
                            </Link>
                          </div>)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>)}

        {noForms ? <div className="mt-6 flex flex-col items-center justify-center">
            <h3 className="mt-4 text-lg font-bold text-primary/90">
              {translations[locale]["institution"]["noFormsFoundTitle"]}
            </h3>
            <p className="mt-2 text-center text-primary/90">
              {translations[locale]["institution"]["searchAlternateNameDescription"]}
            </p>
            <Frown size={64} className="my-8 text-primary/90" />
          </div> : <div className="pt-2">
            <Notification message={translations[locale]["institution"]["missingFormInquiry"]} type="info" />
          </div>}
      </CardContent>
    </Card>;
};